/**
 * hubUi.ts — the 課務總覽 redesign's display rules (Hub UI, plan `tasks/todo-hub-ui.md` W0).
 *
 * WHY THIS FILE EXISTS: the PO asked for one design language inside 課務總覽 — "each
 * label, button styles, need to be consistent … the design style needs to be
 * centralized" (docs/HUB_UI_REDESIGN_BRIEF.md § 1). Every page colour, status → colour
 * row, button kind and conditional-panel look lives here; components only read it
 * (CLAUDE.md § 4b). The palette values themselves are CSS tokens in `css/app.scss`
 * (`--pay-page-*`, `--pay-chip-*`, `--pay-band-*`, `--pay-flag-*`); this file names them.
 *
 * Source of truth for the values: docs/HUB_UI_STYLE_SHEET.md (§ 1 families, § 2 page
 * identity, § 3 status → colour, § 5 icons) and docs/HUB_UI_TEXT_FIXES.md (strings).
 *
 * Re-exported by `config/appConfig.ts`. It deliberately imports NOTHING from
 * appConfig: appConfig re-exports this module, so a value read at module load here
 * would hit the circular-import TDZ. Sentences that contain a business number are
 * functions of a `HubTextConfig` the caller builds (utils/hubIdentity.ts).
 */

// ── Window frame (W0.0, PO Q1 2026-09-28) ──────────────────────────────────────
/** 課務總覽 window size — PO: 「increase modal width and height…especially height」. */
export const HUB_WINDOW = {
  WIDTH: 'min(96vw, 1440px)',
  /**
   * Height of the tab body (main tab strip + the pane that scrolls under the pinned
   * stack): ≈ 85vh, capped so the window's own header and 關閉 footer (~220px with
   * the card padding) still fit — on a 1366×768 laptop 85vh alone would push the
   * footer off-screen.
   */
  BODY_HEIGHT: 'min(85vh, calc(100vh - 220px))',
} as const;
// Phones (≤ 640px, PO 2026-09-29 option A 「視窗滿版」): neither value applies — the window
// is full screen (no outer margin, the full dynamic viewport height 100dvh) and the tab
// body takes whatever the header and the 關閉 footer leave. Same breakpoint as the phone
// layout in css/app.scss; the rules live in ActiveStatusPanelModal.vue.

// ── Colour families (style sheet § 1 — the only palette) ──────────────────────
/**
 * One family = one meaning. `red2` = archived by the system (strong red); `grey` =
 * neutral facts and human decisions (不續報, 來源); `slate` = archived page identity
 * (近期未處理) and its band; `yellow` = page identity of 待通知 / 安排中 only (chips
 * stay amber); `blue` = the theme; `blueState` = in process / seat held.
 */
export const HUB_FAMILIES = [
  'red',
  'red2',
  'amber',
  'yellow',
  'orange',
  'green',
  'blue',
  'blueState',
  'cyan',
  'purple',
  'violet',
  'grey',
  'slate',
  'rose',
] as const;
export type HubFamily = (typeof HUB_FAMILIES)[number];

/** CSS custom-property names (never hex) of one family in one role. */
export interface HubFamilyTokens {
  /** Page identity, selected sub-tab, panel tint: `--pay-page-*`. */
  page: { bg: string; text: string; border: string; icon: string };
  /** Status chips (chip builder) and HubFlag in the window: `--pay-chip-*`. */
  chip: { bg: string; text: string; border: string };
}

function pageTokens(slug: string): HubFamilyTokens['page'] {
  return {
    bg: `--pay-page-${slug}-bg`,
    text: `--pay-page-${slug}-text`,
    border: `--pay-page-${slug}-border`,
    icon: `--pay-page-${slug}-icon`,
  };
}
function chipTokens(slug: string): HubFamilyTokens['chip'] {
  return {
    bg: `--pay-chip-${slug}-bg`,
    text: `--pay-chip-${slug}-text`,
    border: `--pay-chip-${slug}-border`,
  };
}

/**
 * Family → token names. The chip role reuses the chip tokens the 排課時段表 chip
 * builder already reads (their values are unchanged unless the style sheet's V-list
 * says so), so a family keeps ONE hex per value across the page and chip roles
 * (`--pay-page-*` aliases the chip token wherever the value is the same).
 */
export const HUB_COLOR_FAMILIES: Record<HubFamily, HubFamilyTokens> = {
  red: { page: pageTokens('red'), chip: chipTokens('warning') },
  red2: { page: pageTokens('red2'), chip: chipTokens('archive') },
  amber: { page: pageTokens('amber'), chip: chipTokens('normal') },
  yellow: { page: pageTokens('yellow'), chip: chipTokens('grace') },
  orange: { page: pageTokens('orange'), chip: chipTokens('contacted') },
  green: { page: pageTokens('green'), chip: chipTokens('done') },
  blue: { page: pageTokens('blue'), chip: chipTokens('theme') },
  blueState: { page: pageTokens('blue-state'), chip: chipTokens('transferring') },
  cyan: { page: pageTokens('cyan'), chip: chipTokens('external') },
  purple: { page: pageTokens('purple'), chip: chipTokens('special-hold') },
  violet: { page: pageTokens('violet'), chip: chipTokens('verification') },
  grey: { page: pageTokens('grey'), chip: chipTokens('missed') },
  slate: { page: pageTokens('slate'), chip: chipTokens('missed') },
  rose: { page: pageTokens('rose'), chip: chipTokens('review') },
};

/** `var(--token)` for a token name — the only way components turn a name into CSS. */
export function hubCssVar(token: string): string {
  return `var(${token})`;
}

// ── Icons (style sheet § 5 — Material Icons names the app already ships) ──────
export const HUB_ICONS = [
  'fact_check',
  'notifications',
  'swap_calls',
  'swap_horiz',
  'format_list_numbered',
  'error',
  'hourglass_empty',
  'mail',
  'folder',
  'lock',
  'sync',
  'check_circle',
  'warning',
  'person',
  'inbox',
  'edit',
  'history',
  'history_toggle_off',
  'more_vert',
  'open_in_new',
  'refresh',
  'search',
  'arrow_back',
  'add',
  'check',
  'event_seat',
  'flag',
  'schedule',
  'link',
  'expand_more',
  'expand_less',
  'event_available',
  'info',
  // 轉班 原班／新班 (M5, PO 2026-09-29): leaving the old class · joining the new one.
  'logout',
  'login',
] as const;
export type HubIcon = (typeof HUB_ICONS)[number];

// ── Numbers the identity sentences quote (built by utils/hubIdentity.ts) ───────
export interface HubTextConfig {
  lsedReviewMinCount: number;
  autoTagThreshold: number;
  transferAging: {
    urgent: { amber: number; red: number };
    standard: { amber: number; red: number };
  };
  transferBfGraceDays: number;
  transferBfWarningDays: number;
  transferCompletedRetentionDays: number;
  /** PO 2026-10-03: 未完成 自動歸檔 — the day an unhandled 未完成 closes by itself. */
  transferExpiredAutoCloseDays: number;
}
export type HubText = string | ((cfg: HubTextConfig) => string);

// ── Page identity (style sheet § 2; W0.1) → config/hubPageIdentity.ts (W8.3 split) ──────────
// HUB_PAGE_IDENTITY, HUB_IDENT_INFO and the HubPageKey / HubPageLink / HubPageIdentity types
// live there; appConfig re-exports both files.

// ── Status → colour (style sheet § 3 rows 1–48; W0.2) ─────────────────────────
/**
 * filled = a state (tint background); outline = a "check this" flag (white background,
 * family border and text). Every label of a row lives in its 注意 column.
 */
export type HubFlagForm = 'filled' | 'outline';

export interface HubStatusStyle {
  family: HubFamily;
  icon?: HubIcon;
  form: HubFlagForm;
  /**
   * Explicit chip-token prefix when the chip keeps a token set of its own that is
   * not its family's default (e.g. 結束後新約 = the unchanged red-outline
   * `--pay-chip-conflict-*`). Absent = the family's chip tokens.
   */
  chip?: string;
}

/** Keyed by a stable id; the label text itself is built by the caller (it carries numbers). */
export const HUB_STATUS_FAMILY = {
  // 1 待通知 (剩 N 天 / 今日到期) — the same three zones as the window's bands (V1)
  pendingGreen: { family: 'green', icon: 'notifications', form: 'filled' },
  pendingAmber: { family: 'amber', icon: 'notifications', form: 'filled' },
  pendingRed: { family: 'red', icon: 'notifications', form: 'filled' },
  // 2 已通知·待確認 (V2)
  contacted: { family: 'orange', icon: 'hourglass_empty', form: 'filled' },
  // 3 M/D 首次通知 (Name)
  firstContact: { family: 'green', icon: 'check', form: 'filled' },
  // 4 約課通知待發 (V3)
  notifyPending: { family: 'blue', icon: 'mail', form: 'filled' },
  // 5 已過期未通知 · 名額已釋出
  pastUnnotified: { family: 'red2', icon: 'error', form: 'filled' },
  // 6 已通知·已逾期
  contactedLapsed: { family: 'red2', icon: 'history', form: 'filled' },
  // 7a 課務未通知 · 期限內未結案 · 完成續報未上傳 (V6)
  archivedBySystem: { family: 'red2', icon: 'warning', form: 'filled' },
  // 7b 不續報 — a human decision (V6)
  notRenewing: { family: 'grey', form: 'filled' },
  // 8 暫時保留名額
  specialHold: { family: 'purple', icon: 'lock', form: 'filled' },
  // 9 名額保留中·目前無約課
  seatHeldNoBookings: { family: 'blueState', icon: 'event_seat', form: 'filled' },
  // 10 學期至 M/D
  termUntil: { family: 'blueState', icon: 'flag', form: 'filled' },
  // 11–14 "check this" flags (V7)
  bookedDuringTerm: { family: 'amber', icon: 'warning', form: 'outline' },
  lsedUpdated: { family: 'amber', icon: 'warning', form: 'outline' },
  deadlineChanged: { family: 'amber', icon: 'warning', form: 'outline' },
  // 期限已改為 M/D when 保留至 moved EARLIER than the date the parent was told (W10.4 F11, D26
  // (11), PO 2026-10-14; spec E-03 (c) — the row is in 今天要再次提醒 that day): red outline.
  deadlineChangedEarlier: { family: 'red', icon: 'warning', form: 'outline' },
  systemLsed: { family: 'amber', icon: 'warning', form: 'outline' },
  // 15 疑似換時段 (+ 原 … · M/D 結束)
  suspectedSlotChange: { family: 'cyan', icon: 'swap_horiz', form: 'filled' },
  // 16 結束後新約 — unchanged red outline
  bookedAfterEnd: { family: 'red', icon: 'error', form: 'outline', chip: 'conflict' },
  // 17 老師建議轉出 / 老師建議轉班 (V4)
  teacherSuggest: { family: 'cyan', icon: 'swap_horiz', form: 'filled' },
  // 18 轉出中 · 轉入中 / 轉入待 Excel
  transferring: { family: 'blueState', icon: 'sync', form: 'filled' },
  transferAwaitingExcel: { family: 'blueState', icon: 'hourglass_empty', form: 'filled' },
  // 19 已轉出
  transferredOut: { family: 'grey', form: 'filled' },
  // 20 轉班未完成 (V5)
  transferIncomplete: { family: 'red', icon: 'warning', form: 'filled' },
  // 54 新班已保留名額 (M/D 起) — a 未完成 whose new class is a real live enrollment again
  // (Excel revived it; deep review M1): the transfer succeeded late, 已處理 closes it. Also on
  // 安排中 when Excel already booked a full term in a proposed class (L5, PO 2026-10-04).
  targetAlive: { family: 'green', icon: 'check_circle', form: 'filled' },
  // 21 原時段又約 N 堂 (V7)
  sourceBookedAgain: { family: 'amber', icon: 'warning', form: 'outline' },
  // 22 新時段已約 N 堂 / 新時段共約 N 堂 — progress, not a warning (V7)
  targetBooked: { family: 'blueState', icon: 'flag', form: 'filled' },
  // 23 緊急 (轉班) — no animation (V8)
  urgent: { family: 'red2', form: 'filled' },
  // 24 原班已無約課，名額已釋出
  sourceSeatReleased: { family: 'amber', icon: 'warning', form: 'outline' },
  // 25 待驗證 (legacy)
  verification: { family: 'violet', form: 'filled' },
  // 26 來源：續報 · 轉班 · 新學期 — a fact (V9)
  source: { family: 'grey', form: 'filled' },
  // 27 (retired 2026-10-03: 等 Excel 上傳 — its renewal-excel-wait state was removed)
  // 28 (retired 2026-09-29: the 候補 已體驗 · 未體驗 toggle was removed — the 類型 decides the price)
  // 29 可提供 M/D … · 保留名額 N/N (候補)
  offerDate: { family: 'green', form: 'filled' },
  heldSeats: { family: 'blueState', form: 'filled' },
  // 30 約課 N 堂 · 無近期約課 · 已上 N 堂 (學生紀錄)
  bookedCount: { family: 'green', form: 'filled' },
  noUpcoming: { family: 'grey', form: 'filled' },
  attendedCount: { family: 'grey', form: 'filled' },
  // 31 待確認 N (排課時段表 header chip) (V10)
  review: { family: 'rose', icon: 'fact_check', form: 'filled' },
  // 33–48 — labels the style sheet lacked (plan review, Q6 approved)
  sameSlotReturn: { family: 'cyan', form: 'filled' },
  brandNewSlot: { family: 'cyan', form: 'filled' },
  suspectedSlotChangeHeld: { family: 'cyan', icon: 'swap_horiz', form: 'filled' },
  newAfterDismiss: { family: 'cyan', icon: 'warning', form: 'outline' },
  multiSlotCompare: { family: 'amber', icon: 'warning', form: 'outline' },
  transferInProgress: { family: 'blueState', icon: 'sync', form: 'filled' },
  notNotified: { family: 'red2', form: 'filled' },
  notifiedOn: { family: 'green', icon: 'check', form: 'filled' },
  arrangedAwaitingExcel: { family: 'blueState', icon: 'hourglass_empty', form: 'filled' },
  expired: { family: 'red', icon: 'warning', form: 'filled' },
  completed: { family: 'green', icon: 'check', form: 'filled' },
  manualConfirmed: { family: 'grey', form: 'filled' },
  // 44 學生重複預約 — replaced by 50 已另報 (PO 2026-09-29); its key is gone with the flag.
  // 45 原時段已下架 — the source left the timetable mid-transfer, it must be handled: red
  // outline + warning (PO 2026-09-29, design § 4 B2; was grey filled).
  sourceSlotRemoved: { family: 'red', icon: 'warning', form: 'outline' },
  // 55 新班時段已下架 — a 未完成 the backend re-routed because the 課表 upload removed its new
  // class (deep review L1): the same red outline as 原時段已下架.
  targetSlotRemoved: { family: 'red', icon: 'warning', form: 'outline' },
  sourceAutoExtended: { family: 'amber', icon: 'warning', form: 'outline' },
  shortOfLessons: { family: 'grey', form: 'filled' },
  noClasses: { family: 'grey', form: 'filled' },
  // 待確認 handled rows (W3.2): the outcome of a multi-slot student's decided row.
  reviewLsedSet: { family: 'green', icon: 'check', form: 'filled' },
  reviewDismissed: { family: 'grey', form: 'filled' },
  // 52 轉班轉入 — the 候補 登記 line of a family 轉為候補 moved over (a fact, like 43).
  fromTransfer: { family: 'grey', form: 'filled' },
  // 53 重排 ×N — the row was put at the end of its line N times (a fact, like 52; M3).
  requeued: { family: 'grey', form: 'filled' },
  // 50 ⇄ 已另報 {slot} — another active 學期 enrollment started after the row's event
  // (Excel data = cyan; FILLED so it stands out on the cyan-tinted row, PO 2026-09-29 2A);
  // the design's darker, bold cyan (M2 review: --pay-chip-elsewhere-*, strong).
  enrolledElsewhere: { family: 'cyan', icon: 'swap_horiz', form: 'filled', chip: 'elsewhere' },
  // 58 已在候補時段報名 — a linked 候補 row whose student's new 學期 enrollment is in one of
  // the row's own 可上時段 (PO 2026-09-30, 9B-5): the green of 54 新班已保留名額 (targetAlive).
  enrolledInWaitedSlot: { family: 'green', icon: 'check_circle', form: 'filled' },
  // 59 候補中 {班級} — a 續報 row whose student waits in an open linked 候補 row (9B-5): a
  // fact, grey like 52 轉班轉入.
  waitlisting: { family: 'grey', form: 'filled' },
  // 60 續報已通知 — a 轉班 whose old class had a 首次通知 when 登記轉班 fired (續報・轉班 完成規則
  // W5.5, G-12: 「白色標籤」 — white background, grey text and border).
  renewalKept: { family: 'grey', form: 'outline' },
  // 約課切斷確認 (tasks/todo-lsed-same-sitting.md): 待確認 › 約課切斷確認's 轉班 stage tag (轉班 安排中
  // / 轉班 安排中 · 新班) beside the row's ⚠ while an open 轉班 locks the class — grey, a fact
  // (D50, N30 (ii); the look of 52 轉班轉入).
  cutoffTransferStage: { family: 'grey', form: 'filled' },
  // 約課切斷確認 Spec 14 (e), W3.8: the 轉班 row's 「約課可能被切斷」 hint beside 原時段又約 N 堂 —
  // the amber "check this" outline + ⚠ (V7; the look of 14 系統偵測續報).
  cutoffHint: { family: 'amber', icon: 'warning', form: 'outline' },
} as const satisfies Record<string, HubStatusStyle>;
export type HubStatusKey = keyof typeof HUB_STATUS_FAMILY;

// ── 轉班 原班／新班 column (M5, PO 2026-09-29; design transfer-to-waitlist.html § 6) ──
/**
 * One line per class, a leading icon: `logout` = 原班 (the slot's link, as every 時段 link);
 * `login` = 新班 — a 安排中 proposal grey, the 待約課 hold blue (in process / seat held),
 * 已完成 green, 未完成 grey and struck through (its hold ran out). The icon takes the line's
 * colour; its hover names what the line is. Colours are CSS tokens (never hex).
 */
export type HubTransferClassTone = 'source' | 'proposal' | 'hold' | 'done' | 'failed';

export interface HubTransferClassToneSpec {
  icon: HubIcon;
  /** The icon's hover and accessible name. */
  title: string;
  /** Text colour token; `null` = the 時段 link colour (原班). */
  text: string | null;
  /** Icon colour token. */
  iconColor: string;
  strong: boolean;
  strike: boolean;
}

export const HUB_TRANSFER_CLASS_TONE: Record<HubTransferClassTone, HubTransferClassToneSpec> = {
  source: {
    icon: 'logout',
    title: '原班',
    text: null,
    iconColor: '--pay-page-slate-icon',
    strong: false,
    strike: false,
  },
  proposal: {
    icon: 'login',
    title: '新班 (提議)',
    text: '--pay-hub-muted',
    iconColor: '--pay-hub-muted',
    strong: false,
    strike: false,
  },
  hold: {
    icon: 'login',
    title: '新班 (轉入待 Excel)', // TRANSFER_IN_HOLD_LABEL (PO 2026-10-06 「go with A」; appConfig imports this file)
    text: '--pay-page-blue-state-text',
    iconColor: '--pay-page-blue-state-text',
    strong: true,
    strike: false,
  },
  done: {
    icon: 'login',
    title: '新班 (已完成)',
    text: '--pay-zone-green-text',
    iconColor: '--pay-zone-green-text',
    strong: true,
    strike: false,
  },
  failed: {
    icon: 'login',
    title: '新班 (已過期)',
    text: '--pay-hub-muted',
    iconColor: '--pay-hub-muted',
    strong: false,
    strike: true,
  },
};

// ── Conditional panels (style sheet § 4.7; W0.7) ──────────────────────────────
/** `hold` = the 候補 保留名額 family (violet, `--pay-hub-panel-hold-*`; PO 2026-09-30). */
export type HubPanelVariant = 'cyan' | 'green' | 'priority' | 'hold';
export type HubPanelKey =
  | 'renewalPendingPriority'
  | 'lsedReviewNewSlot'
  | 'waitlistPlaceable'
  | 'waitlistHolding';

export interface HubPanelSpec {
  variant: HubPanelVariant;
  icon: HubIcon;
  title: string;
  caption: HubText;
}

/**
 * A panel appears only when it has rows and never uses its page's family (style
 * sheet § 2 rule): 新建議 / 新時段報名待確認 / 有名額可上 = cyan; the 待通知 priority
 * panel = solid dark red.
 */
export const HUB_PANEL_VARIANT: Record<HubPanelKey, HubPanelSpec> = {
  renewalPendingPriority: {
    variant: 'priority',
    icon: 'event_seat',
    title: '已無約課但名額仍保留 (優先確認)',
    caption: 'Excel 上這些學生之後沒有約課了，但名額還保留著：先確認 (可能馬上續報，也可能要處理)',
  },
  lsedReviewNewSlot: {
    variant: 'cyan',
    icon: 'warning',
    title: '新時段報名待確認',
    // P1 (PO 2026-10-02): 學期票券, and 3 到 7 堂 (8+ is created by detection, so it never
    // reaches this panel) — 「3 到 7 堂」 as 既有報名待確認's caption (B5, PO 2026-10-03).
    caption: (cfg) =>
      `沒有在籍報名的學生於此時段有 ${cfg.lsedReviewMinCount} 到 ${cfg.autoTagThreshold - 1} 堂學期票券約課：換時段、回流、或全新報名`,
  },
  waitlistPlaceable: {
    variant: 'cyan',
    icon: 'check',
    title: '有名額可上',
    caption: '這些家庭現在就有日期可以推薦',
  },
  /**
   * 候補 保留名額 stage 2 (PO 2026-09-30, design § C): families holding a seat while the BF
   * booking waits for the Excel — above 有名額可上. The caption names the button by its
   * label (review H-11).
   */
  waitlistHolding: {
    variant: 'hold',
    icon: 'hourglass_empty',
    title: '保留中 · 待 Excel 上傳',
    caption: () => `上傳後確認家長已在名單中，再按「${HUB_MUTATING_ACTIONS.scheduled.label}」`,
  },
};

// ── Buttons (style sheet § 4.8; W0.10, Q7: the site theme colours) ────────────
/**
 * primary (theme blue, filled) = go forward / notify · success (green, filled) =
 * confirm / complete · error (red outline) = reject / remove · default (white
 * outline) = other · quiet (no border) = more / edit / history. One filled button
 * per row. Row buttons are native `<button>`s (HubActionButton); n-button only in
 * toolbars and footers (D-8).
 */
export type HubButtonKindName = 'primary' | 'success' | 'error' | 'default' | 'quiet';

export interface HubButtonSpec {
  kind: HubButtonKindName;
  /** true = the click itself writes (after its confirm) → an in-flight guard is required. */
  mutating: boolean;
  /** Visible label; absent for icon-only buttons (they carry ariaLabel). */
  label?: string;
  icon?: HubIcon;
  ariaLabel?: string;
}

/** Controls whose click writes (behind their confirm) — `inFlight` is required. */
export const HUB_MUTATING_ACTIONS = {
  releaseSeatConfirmed: { kind: 'default', mutating: true, label: '已確認釋出名額' },
  removeSpecialHold: { kind: 'error', mutating: true, label: '移除暫時保留' },
  markHandled: { kind: 'default', mutating: true, label: '已處理' },
  /** 約課通知待發's row button beside 發約課通知 (W10.4 F15, D26 (15) — was a ⋮ entry). */
  skipNotify: { kind: 'default', mutating: true, label: '無需通知' },
  /**
   * 2026-10-13 (續報・轉班 完成規則 W2.1 / W2.4, E-00 / D-01b): no word here — the row passes
   * `label` from appConfig RESERVATION_ACTION_LABEL_BY_KIND (the ONE home of the words; hubUi
   * cannot import appConfig, which re-exports this file): 確認保留名額 (待確認 › 新時段報名待確認,
   * kind 'create'); setLsed = 修改最後約課日 / 恢復保留名額 by the row's kind (待確認 › 既有報名),
   * white — the second button beside 結算續報 (C-04 次鈕).
   */
  confirmEnrollment: { kind: 'success', mutating: true },
  setLsed: { kind: 'default', mutating: true },
  /**
   * 2026-10-13 (續報・轉班 完成規則 W0.3, D-01 / D-02 / C-04): 結算續報 — opens the 修改最後約課日
   * window with the last booking preselected (entry 'settle'); the server decides 結算 by fact
   * (P4). The ONE home of the word: appConfig RESERVATION_ACTION_LABEL_BY_KIND.settle reads it.
   */
  settleRenewal: { kind: 'success', mutating: true, label: '結算續報' },
  /**
   * 2026-10-24 (約課切斷確認 Spec 2 / 5, D27): 補正最後約課日 — the 待確認 › 約課切斷確認 row's main
   * button (blue, mockup § 2); opens the two-step window whose 確認 writes (entry 'cutoffFix').
   * The ONE home of the word: the window title (appConfig UI_STRINGS.HUB.REVIEW.CUTOFF.WINDOW.TITLE)
   * reads it.
   */
  fixCutoffLsed: { kind: 'primary', mutating: true, label: '補正最後約課日' },
  dismiss: { kind: 'default', mutating: true, label: '忽略' },
  restore: { kind: 'default', mutating: true, label: '恢復' },
  cancelTransfer: { kind: 'default', mutating: true, label: '取消轉班' },
  extendTransferHold: { kind: 'default', mutating: true },
  convertToNotRenewing: { kind: 'default', mutating: true, label: '轉為不續報' },
  scheduled: { kind: 'success', mutating: true, label: '已安排' },
  giveUp: { kind: 'error', mutating: true, label: '放棄' },
  undo: { kind: 'default', mutating: true, label: '復原' },
  /** 候補 保留名額 stage 2 (PO 2026-09-30): end the hold, the row stays on the list. */
  cancelHold: { kind: 'default', mutating: true, label: '取消保留' },
  /**
   * 2026-10-15 (續報・轉班 完成規則 W10.9, D36): 家長留原班 — beside a 轉班 row's normal button when
   * the old class was booked again (utils/transferRefill); white (not clicking is a valid choice —
   * D36 (a)). 8+ writes behind its 確認 bubble; 3–7 opens the 結算續報 window for the old class.
   */
  parentKeptSource: { kind: 'default', mutating: true, label: '家長留原班' },
} as const satisfies Record<string, HubButtonSpec>;

/** Controls whose click only opens a window / menu / form (the write happens inside). */
export const HUB_OPEN_ACTIONS = {
  notifyRenewal: { kind: 'primary', mutating: false, label: '通知續報', icon: 'notifications' },
  extendAndNotify: { kind: 'primary', mutating: false, label: '延長並通知續報' },
  remindParent: {
    kind: 'default',
    mutating: false,
    label: '再次提醒家長',
    icon: 'notifications',
  },
  otherHandling: { kind: 'quiet', mutating: false, icon: 'more_vert', ariaLabel: '其他處理' },
  /** 其他處理 as a labelled button — 近期未處理, where it is the row's action (text-fix A3.5). */
  otherHandlingButton: { kind: 'default', mutating: false, label: '其他處理' },
  sendBookingNotice: { kind: 'primary', mutating: false, label: '發約課通知', icon: 'mail' },
  /** W7.3 (J-02): the 老師建議 row's one button, the 處理 window (was 加入安排名單 / 拒絕). */
  handleSuggestion: { kind: 'primary', mutating: false, label: '處理' },
  confirmTransferIn: { kind: 'success', mutating: false, label: '確定轉入' },
  // 2026-10-15 (續報・轉班 完成規則 D43 A1, PO 「make 結算轉班 blue」): blue go-forward (was white);
  // 結算續報 stays green. A blocked one stays light grey (.hub-btn--blocked).
  settleTransfer: { kind: 'primary', mutating: false, label: '結算轉班' },
  /** 轉為候補 (PO 2026-09-29): opens the pre-filled 候補 window; its 儲存 writes. */
  convertToWaitlist: { kind: 'default', mutating: false, label: '轉為候補' },
  editProposal: { kind: 'quiet', mutating: false, icon: 'edit', ariaLabel: '編輯提案' },
  recommendMessage: { kind: 'default', mutating: false, label: '推薦訊息' },
  /**
   * 候補 stage 1 (PO 2026-09-30): opens the 保留名額 window — the write is its 確認. Blue =
   * go forward (style sheet § 4.8; the row's one filled button).
   */
  holdSeat: { kind: 'primary', mutating: false, label: '保留名額' },
  rowMore: { kind: 'quiet', mutating: false, icon: 'more_vert', ariaLabel: '更多' },
  reload: { kind: 'default', mutating: false, label: '重新載入', icon: 'refresh' },
} as const satisfies Record<string, HubButtonSpec>;

/**
 * The ✎ of a 待約課 row (TransferChangeTargetForm's 修改新班時段 — an editProposal-look icon
 * button with this aria-label): named here so the 待約課 sentence and
 * UI_STRINGS.HUB.TRANSFER.CHANGE_TARGET read one label (fix round 1, LOW 16).
 */
export const HUB_CHANGE_TARGET_LABEL = '修改新班時段';

/** W7.4 (J-03): 老師建議 › 處理's four words — read by the sentence above and appConfig. */
export const HUB_TRANSFER_HANDLE_LABEL = {
  registerTransfer: '登記轉班',
  toWaitlist: '轉為候補',
  handledElsewhere: '已另行安排',
  reject: '不採用',
} as const;

export const HUB_BUTTON_KIND = { ...HUB_MUTATING_ACTIONS, ...HUB_OPEN_ACTIONS } as const;
export type HubMutatingActionId = keyof typeof HUB_MUTATING_ACTIONS;
export type HubOpenActionId = keyof typeof HUB_OPEN_ACTIONS;
export type HubActionId = HubMutatingActionId | HubOpenActionId;

/** 再次提醒家長's reminder states (useSessionEnrollments/renewalAwaitingDisplay.ts). */
export type HubReminderState = 'later' | 'today' | 'overdue' | 'passed' | 'done' | 'none';

/**
 * The kind a button renders with. Only 再次提醒家長 depends on state: filled blue
 * when the row is counted today (today / overdue), white otherwise — incl. `passed`, a
 * reminder day the shared rule does not count (F-03) (PO round 2: one filled button per
 * row, and only when it is the thing to do now).
 */
export function resolveHubButtonKind(
  action: HubActionId,
  reminderState?: HubReminderState,
): HubButtonKindName {
  if (action === 'remindParent') {
    return reminderState === 'today' || reminderState === 'overdue' ? 'primary' : 'default';
  }
  return HUB_BUTTON_KIND[action].kind;
}

export function isHubMutatingAction(action: HubActionId): action is HubMutatingActionId {
  return HUB_BUTTON_KIND[action].mutating;
}
